<template>
  <div class="document-results">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h2 class="page-title">{{ projectInfo?.title || '文档翻译结果' }}</h2>
        </div>
      </div>
    </div>

    <!-- 项目信息卡片 -->
    <a-card class="project-info-card" :bordered="false">
      <a-descriptions :column="3" size="small">
        <a-descriptions-item label="项目状态">
          <a-tag :color="getStatusColor(projectInfo?.status)">
            {{ getStatusText(projectInfo?.status) }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="源语言">
          {{ getLanguageText(projectInfo?.original_language) }}
        </a-descriptions-item>
        <a-descriptions-item label="目标语言">
          <a-tag 
            v-for="lang in projectInfo?.target_languages" 
            :key="lang"
            color="blue"
            size="small"
          >
            {{ getLanguageText(lang) }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="创建时间">
          {{ formatDateTime(projectInfo?.created_at) }}
        </a-descriptions-item>
        <a-descriptions-item label="完成时间">
          {{ formatDateTime(projectInfo?.updated_at) }}
        </a-descriptions-item>
        <a-descriptions-item label="文档类型">
          {{ getDocumentTypeText(projectInfo?.document_type) }}
        </a-descriptions-item>
      </a-descriptions>
    </a-card>

    <!-- 翻译结果展示 -->
    <div class="results-content">
      <div class="document-panels">
        <!-- 左侧：源文档 -->
        <div class="panel source-panel">
          <a-card title="源文档" size="small" :bordered="false" class="document-card">
            <div class="panel-content">
              <DocumentViewer 
                v-if="sourceDocumentUrl && sourceFileType === 'pdf'"
                :document-url="sourceDocumentUrl"
                :file-type="sourceFileType"
                :enable-sync-scroll="false"
              />
              <div v-else-if="!sourceDocumentUrl" class="no-document">
                <a-empty description="文档未加载" />
              </div>
              <div v-else class="no-document">
                <a-result
                  status="warning"
                  title="不支持的文件格式"
                  :sub-title="`当前文件格式 (.${sourceFileType}) 不支持在线预览，请下载后查看`"
                />
              </div>
            </div>
          </a-card>
        </div>

        <!-- 右侧：翻译结果 -->
        <div class="panel target-panel">
          <a-card title="翻译结果" size="small" :bordered="false" class="document-card">
            <div class="panel-content">
              <DocumentViewer 
                v-if="targetDocumentUrl && targetFileType === 'pdf'"
                :document-url="targetDocumentUrl"
                :file-type="targetFileType"
                :enable-sync-scroll="false"
              />
              <div v-else-if="!targetDocumentUrl" class="no-document">
                <a-empty description="译文未生成" />
              </div>
              <div v-else class="no-document">
                <a-result
                  status="warning"
                  title="不支持的文件格式"
                  :sub-title="`当前文件格式 (.${targetFileType}) 不支持在线预览，请下载后查看`"
                />
              </div>
            </div>
          </a-card>
        </div>
      </div>
    </div>

    <!-- 热词统计 -->
    <a-card title="热词统计" size="small" class="hotword-stats-card">
      <div v-if="hotwordStats" class="stats-content">
        <a-row :gutter="16">
          <a-col :span="6">
            <a-statistic 
              title="总热词数" 
              :value="hotwordStats.total_count" 
              :value-style="{ color: '#1890ff' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="AI建议" 
              :value="hotwordStats.ai_suggested_count" 
              :value-style="{ color: '#52c41a' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="已编辑" 
              :value="hotwordStats.edited_count" 
              :value-style="{ color: '#faad14' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="译员确认" 
              :value="hotwordStats.confirmed_count" 
              :value-style="{ color: '#722ed1' }"
            />
          </a-col>
        </a-row>
      </div>
      <div v-else class="empty-stats">
        <a-empty description="暂无热词统计" />
      </div>
    </a-card>

    <!-- 产物下载 -->
    <a-card title="产物下载" size="small" class="download-options-card">
      <div class="download-options">
        <a-space direction="vertical" size="large" style="width: 100%">
          <div class="download-item">
            <div class="download-info">
              <h4>翻译后文档</h4>
              <p>包含完整翻译结果的文档文件</p>
            </div>
            <a-button type="primary" @click="downloadTranslatedDocumentHandler">
              <template #icon><FileTextOutlined /></template>
              下载翻译文档
            </a-button>
          </div>
        </a-space>
      </div>
    </a-card>

    <WorkItemReportCard
      :work-item-id="projectId"
      kind="document"
      :file-name-prefix="projectInfo?.title || 'document'"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  FileTextOutlined
} from '@ant-design/icons-vue'
import { 
  getDocumentProject,
  downloadTranslatedDocument,
  getDocumentPdfPreviewUrl,
  type DocumentProject
} from '@/modules/document/api'
import { hotWordApi, type HotWordStats } from '@/modules/system/api/hotword'
import DocumentViewer from '@/modules/document/components/DocumentViewer.vue'
import WorkItemReportCard from '@/modules/report/components/WorkItemReportCard.vue'
import dayjs from 'dayjs'

const route = useRoute()

// 基础状态
const projectId = computed(() => parseInt(route.params.id as string))
const loading = ref(false)

// 项目信息
const projectInfo = ref<DocumentProject>()
const hotwordStats = ref<HotWordStats>()

// 文档预览相关
const sourceDocumentUrl = ref('')
const targetDocumentUrl = ref('')
const sourceFileType = ref('')
const targetFileType = ref('')

// 生命周期
onMounted(async () => {
  await loadProjectData()
})

// 方法实现
const loadProjectData = async () => {
  try {
    loading.value = true
    
    // 加载项目信息
    const project = await getDocumentProject(projectId.value)
    projectInfo.value = project
    
    // 加载文档预览URL
    await loadDocumentPreview()
    
    // 尝试加载热词统计
    try {
      const statsResponse = await hotWordApi.getHotWordStats(projectId.value, project.target_languages?.[0] || 'en')
      hotwordStats.value = statsResponse
    } catch (error) {
      // 热词统计不存在，使用默认值
      console.log('热词统计不存在，使用默认值')
      hotwordStats.value = null
    }
    
  } catch (error) {
    console.error('加载项目数据失败:', error)
  } finally {
    loading.value = false
  }
}

// 加载文档预览（使用 PDF 预览接口，与翻译页一致）
const loadDocumentPreview = async () => {
  try {
    try {
      sourceDocumentUrl.value = getDocumentPdfPreviewUrl(projectId.value, 'source')
      sourceFileType.value = 'pdf'
      console.log('✅ 原文 PDF 预览 URL:', sourceDocumentUrl.value)
    } catch (error) {
      console.warn('⚠️ 获取原文 PDF 预览失败:', error)
      sourceDocumentUrl.value = ''
      sourceFileType.value = ''
    }

    if (projectInfo.value?.target_languages && projectInfo.value.target_languages.length > 0) {
      try {
        const targetLang = projectInfo.value.target_languages[0]
        targetDocumentUrl.value = getDocumentPdfPreviewUrl(projectId.value, 'target', targetLang)
        targetFileType.value = 'pdf'
        console.log('✅ 译文 PDF 预览 URL:', targetDocumentUrl.value)
      } catch (error) {
        console.warn('⚠️ 译文 PDF 不存在或获取失败:', error)
        targetDocumentUrl.value = ''
        targetFileType.value = ''
      }
    }
  } catch (error) {
    console.error('加载文档预览失败:', error)
  }
}


// 下载翻译文档
const downloadTranslatedDocumentHandler = async () => {
  try {
    // 检查是否有翻译后的文档
    if (!projectInfo.value?.translated_document_storage_id) {
      message.warning('没有找到翻译后的文档，请先完成翻译')
      return
    }
    
    const hideLoading = message.loading('正在下载翻译文档...', 0)
    
    try {
      // 使用 file_storage_id 下载
      const fileStorageId = projectInfo.value.translated_document_storage_id
      const response = await downloadTranslatedDocument(fileStorageId)
      
      hideLoading()
      
      // 从Axios响应中获取Blob数据
      const blob = response.data
      
      // 检查响应是否为有效的 Blob
      if (!(blob instanceof Blob)) {
        console.error('API返回的不是Blob对象:', blob)
        message.error('下载失败：服务器返回的数据格式不正确')
        return
      }
      
      // 检查 Blob 大小
      if (blob.size === 0) {
        console.error('下载的文件为空')
        message.error('下载失败：文件为空')
        return
      }
      
      console.log('下载成功，文件大小:', blob.size, 'bytes')
      
      // 从响应头中提取文件名（支持 RFC 5987 编码）
      let filename = '翻译文档.docx' // 默认文件名
      
      try {
        // 获取 Content-Disposition 头
        const contentDisposition = response.headers?.['content-disposition'] || 
                                   response.headers?.['Content-Disposition'] ||
                                   ''
        
        if (contentDisposition) {
          // 尝试解析 RFC 5987 编码的文件名 (filename*=UTF-8''...)
          const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
          if (rfc5987Match) {
            filename = decodeURIComponent(rfc5987Match[1])
          } else {
            // 尝试解析标准格式 (filename="...")
            const standardMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
            if (standardMatch) {
              filename = standardMatch[1].replace(/^"|"$/g, '') // 移除引号
            }
          }
        }
      } catch (error) {
        // 解析失败，使用默认文件名或从项目信息中获取
      }
      
      // 如果仍然使用默认文件名，尝试从项目信息中获取
      if (filename === '翻译文档.docx') {
        const originalFileName = projectInfo.value.file_name || ''
        if (originalFileName) {
          filename = originalFileName
        } else {
          // 尝试从原文档URL中获取文件名
          const originalDocUrl = projectInfo.value.document_url || projectInfo.value.file_url || ''
          if (originalDocUrl) {
            const urlParts = originalDocUrl.split('/')
            const urlFilename = urlParts[urlParts.length - 1]
            if (urlFilename && urlFilename.includes('.')) {
              filename = urlFilename
            }
          }
        }
      }
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      message.success('翻译文档下载成功！')
      
    } catch (error: any) {
      hideLoading()
      console.error('下载翻译文档失败:', error)
      
      if (error.response) {
        console.error('服务器响应错误:', error.response.status, error.response.data)
      } else if (error.request) {
        console.error('网络请求失败:', error.request)
      } else {
        console.error('请求配置错误:', error.message)
      }
    }
  } catch (error) {
    console.error('下载翻译文档失败:', error)
  }
}

// 辅助方法
const getStatusColor = (status?: string) => {
  const colors: Record<string, string> = {
    draft: 'default',
    pending: 'orange',
    processing: 'processing',
    completed: 'success',
    failed: 'error',
    expired: 'warning',
    cleaned: 'default'
  }
  return colors[status || ''] || 'default'
}

const getStatusText = (status?: string) => {
  const texts: Record<string, string> = {
    draft: '草稿',
    pending: '待执行',
    processing: '执行中',
    completed: '已完成',
    failed: '失败',
    expired: '已过期',
    cleaned: '已清理'
  }
  return texts[status || ''] || '未知'
}

const getLanguageText = (lang?: string) => {
  const languages: Record<string, string> = {
    zh: '中文',
    en: '英文',
    ja: '日文',
    ko: '韩文',
    fr: '法文',
    de: '德文',
    es: '西班牙文',
    ru: '俄文'
  }
  return languages[lang || ''] || lang || '未知'
}

const getDocumentTypeText = (type?: string) => {
  const types: Record<string, string> = {
    word: 'Word文档',
    ppt: 'PowerPoint',
    excel: 'Excel表格',
    txt: '文本文件',
    md: 'Markdown',
    pdf: 'PDF文档'
  }
  return types[type || ''] || type || '未知'
}

const formatDateTime = (dateTime?: string) => {
  if (!dateTime) return '未知'
  return dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss')
}
</script>

<style lang="less" scoped>
.document-results {
  padding: 24px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    
    .header-left {
      .page-title {
        margin: 8px 0 0 0;
        font-size: 24px;
        font-weight: 600;
        color: #262626;
      }
    }
  }
}

.project-info-card {
  margin-bottom: 24px;
}

.results-content {
  margin-bottom: 24px;
  height: calc(100vh - 400px);
  min-height: 600px;
  
  .document-panels {
    display: flex;
    height: 100%;
    width: 100%;
    gap: 1px;
    background: #e8e8e8;
    overflow: hidden;
  }
  
  .panel {
    background: white;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1 1 0;
    min-width: 0;
    
    .document-card {
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      
      :deep(.ant-card-body) {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 0;
      }
      
      :deep(.ant-card-head) {
        padding: 0 16px;
        border-bottom: 1px solid #f0f0f0;
      }
    }
    
    .panel-content {
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0;
      
      .no-document {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: 40px;
      }
    }
  }
  
  .source-panel,
  .target-panel {
    flex: 1 1 0;
    min-width: 0;
  }
}

.hotword-stats-card {
  margin-bottom: 24px;
  
  .stats-content {
    padding: 16px 0;
  }
  
  .empty-stats {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
  }
}

.download-options-card {
  .download-options {
    .download-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;
      background-color: #fafafa;
      border-radius: 8px;
      border: 1px solid #f0f0f0;
      
      .download-info {
        h4 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: #262626;
        }
        
        p {
          margin: 0;
          color: #8c8c8c;
          font-size: 14px;
        }
      }
    }
  }
}
</style>
